import type { RecordModel } from 'pocketbase';
import { ErrorResponseSchema } from '@kioskforge/shared';
import { pb } from './pocketbase';
import { ApiError, bearerValue } from './client';

export const ALLOWED_MEDIA_MIME = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'video/mp4'];
export const MAX_MEDIA_BYTES = 25 * 1024 * 1024;

export type MediaRecord = RecordModel & {
  name?: string;
  mime?: string;
  size?: number;
  width?: number;
  height?: number;
  tags?: string[];
  file?: string;
  thumbnail?: string;
};

export type UploadResult = { media_id: string; mime: string; width?: number; height?: number; has_thumbnail: boolean };

export function mediaFileUrl(record: MediaRecord): string {
  return record.file ? pb.files.getURL(record, record.file) : '';
}

export function mediaThumbUrl(record: MediaRecord): string {
  const name = record.thumbnail || record.file;
  return name ? pb.files.getURL(record, name) : '';
}

export function formatBytes(bytes: number | undefined): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Client-side mirror of the server limits (defence in depth; the server sniffs bytes). */
export function checkFile(file: File): string {
  if (file.size === 0) return 'That file is empty (0 bytes). Choose a picture or video with content.';
  if (file.size > MAX_MEDIA_BYTES) return `Larger than ${formatBytes(MAX_MEDIA_BYTES)}.`;
  if (!ALLOWED_MEDIA_MIME.includes(file.type)) return `Unsupported type ${file.type || 'unknown'}.`;
  return '';
}

/**
 * Upload via the server (magic-byte sniffing + thumbnail). XHR is used so the
 * per-file progress can be reported; the body is the JSON contract the server
 * accepts (`name` + `data_base64`).
 */
export function uploadMedia(file: File, onProgress?: (percent: number) => void): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      const comma = dataUrl.indexOf(',');
      const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/v1/media');
      xhr.setRequestHeader('Content-Type', 'application/json');
      const token = bearerValue();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onerror = () => reject(new Error('Network error during upload.'));
      xhr.onload = () => {
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(parsed as UploadResult);
          return;
        }
        const error = ErrorResponseSchema.safeParse(parsed);
        if (error.success) {
          const { code, message, request_id, details } = error.data.error;
          reject(new ApiError(message, code, request_id, xhr.status, details));
        } else {
          reject(new ApiError(xhr.statusText, 'upload_failed', 'unknown', xhr.status));
        }
      };
      xhr.send(JSON.stringify({ name: file.name, data_base64: base64 }));
    };
    reader.readAsDataURL(file);
  });
}

export async function listMedia(): Promise<MediaRecord[]> {
  const records = await pb.collection('media').getFullList({ sort: '-created' });
  return records as MediaRecord[];
}

export async function deleteMedia(id: string): Promise<void> {
  await pb.collection('media').delete(id);
}

export type MediaUsage = { layouts: number; deployments: number };

/** How many layouts/deployments reference this media item (delete warning). */
export async function checkMediaUsage(id: string): Promise<MediaUsage> {
  const [layouts, deployments] = await Promise.all([
    pb.collection('layouts').getList(1, 1, { filter: `compiled ~ "${id}"` }),
    pb.collection('deployments').getList(1, 1, { filter: `snapshot ~ "${id}"` })
  ]);
  return { layouts: layouts.totalItems, deployments: deployments.totalItems };
}
